// DeckListRoute: Route-level container for the Deck List page.
// It owns routing concerns and passes App-level props down to DeckListPage.
// Use this file to add route-specific logic (e.g., query params) without changing the component.
// DeckListRoute: Route-level container for the Deck List page.
// Owns deck list state and deck CRUD via /api/decks; renders DeckListPage.
import React, { useEffect, useState, useRef, useCallback } from 'react';
import DeckListPage from '../components/DeckListPage';
import ProfileBanner from '../components/ProfileBanner';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { json } from '../api/apiClient';
import { errorMessage } from '../api/errorMessage';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../ui/Toast';

export default function DeckListRoute() {
  const { authToken, logout } = useAuth();
  const { viewedUserId, isOwnProfile } = useProfile();
  const navigate = useNavigate();
  const toast = useToast();

  const [decks, setDecks] = useState([]);
  const [showNewDeckModal, setShowNewDeckModal] = useState(false);
  const [activeTab, setActiveTab] = useState('create');
  const [newDeckName, setNewDeckName] = useState('');
  const [newDeckScryfallIds, setNewDeckScryfallIds] = useState('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState(null);

  const [archidektInput, setArchidektInput] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);

  // Bulk Archidekt Import State
  const [bulkUsername, setBulkUsername] = useState('');
  const [bulkStep, setBulkStep] = useState('input'); // 'input' | 'select' | 'importing' | 'done'
  const [bulkDecks, setBulkDecks] = useState([]); // Available decks from Archidekt user
  const [bulkSelectedIds, setBulkSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [bulkStatus, setBulkStatus] = useState(null); // Progress from polling
  const [bulkImportDone, setBulkImportDone] = useState(false); // One-time gate
  const bulkPollRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDecks = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = viewedUserId ? `/api/decks/list?userId=${viewedUserId}` : '/api/decks/list';
      const data = await json(url);

      // Backend sendet { decks: [...], isOwnProfile: bool } für Freundes-Profile
      if (data.decks) {
        setDecks(data.decks);
      } else {
        // Fallback für alte Antwortstruktur (ohne isOwnProfile)
        setDecks(data);
      }
    } catch (err) {
      if (err && err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('Fehler beim Laden der Decks:', err);
      setError(errorMessage(err, 'Decks konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) fetchDecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, viewedUserId]); // Neu laden wenn viewedUserId ändert

  const handleCreateDeck = async () => {
    if (!newDeckName) {
      setError('Deckname und Scryfall IDs sind erforderlich.');
      return;
    }
    setLoading(true);
    setError(null);
    const scryfallIdArray = newDeckScryfallIds.split(/\r?\n+/).filter((id) => id.trim() !== '');
    try {
      const result = await json('/api/decks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deckName: newDeckName, scryfallIds: scryfallIdArray }),
      });
      setShowNewDeckModal(false);
      setNewDeckName('');
      setNewDeckScryfallIds('');
      toast.push({ tone: 'success', message: `Deck „${result.deckName}" erstellt.` });
      fetchDecks();
    } catch (err) {
      if (err && err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('Fehler beim Erstellen des Decks:', err);
      setError(errorMessage(err, 'Deck konnte nicht erstellt werden.'));
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteDeck = (deck) => {
    setDeckToDelete(deck);
    setShowDeleteConfirmModal(true);
  };

  const handleCancelDeleteDeck = () => {
    setShowDeleteConfirmModal(false);
    setDeckToDelete(null);
  };


  const handleArchidektImport = async () => {
    if (!archidektInput.trim()) {
      setImportError('Bitte eine Archidekt-URL oder Deck-ID eingeben.');
      return;
    }
    setImportLoading(true);
    setImportError(null);
    try {
      const result = await json('/api/decks/import/archidekt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archidektUrl: archidektInput }),
      });
      setShowNewDeckModal(false);
      setArchidektInput('');
      toast.push({ tone: 'success', message: result.message || 'Deck importiert.' });
      fetchDecks();
    } catch (err) {
      if (err && err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('Fehler beim Archidekt-Import:', err);
      setImportError(errorMessage(err, 'Fehler beim Importieren des Decks.'));
    } finally {
      setImportLoading(false);
    }
  };

  // ---- Bulk Import: Preference laden ----
  useEffect(() => {
    if (!authToken || !isOwnProfile) return;
    json('/api/me/preferences')
      .then((data) => {
        if (data?.preferences?.archidektBulkImportDone) {
          setBulkImportDone(true);
        }
      })
      .catch(() => { /* silent */ });
  }, [authToken, isOwnProfile]);

  // ---- Bulk Import: Polling stoppen beim Unmount ----
  useEffect(() => {
    return () => { if (bulkPollRef.current) clearInterval(bulkPollRef.current); };
  }, []);

  const startBulkPolling = useCallback(() => {
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    bulkPollRef.current = setInterval(async () => {
      try {
        const data = await json('/api/decks/import/archidekt/bulk-status');
        const st = data?.status;
        setBulkStatus(st);
        if (st && (st.status === 'done' || st.status === 'error')) {
          clearInterval(bulkPollRef.current);
          bulkPollRef.current = null;
          if (st.status === 'done') {
            setBulkStep('done');
            setBulkImportDone(true);
            toast.push({ tone: 'success', message: 'Profil-Import abgeschlossen.' });
            fetchDecks();
          }
        }
      } catch {
        // silent — retry on next interval
      }
    }, 3000);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkFetchDecks = async () => {
    if (!bulkUsername.trim()) {
      setBulkError('Bitte einen Archidekt-Benutzernamen eingeben.');
      return;
    }
    setBulkLoading(true);
    setBulkError(null);
    try {
      const data = await json('/api/decks/import/archidekt/user-decks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: bulkUsername.trim() }),
      });
      const decks = data?.decks ?? [];
      if (decks.length === 0) {
        setBulkError('Keine öffentlichen Decks für diesen Benutzer gefunden.');
        return;
      }
      setBulkDecks(decks);
      setBulkSelectedIds(new Set(decks.map((d) => d.id)));
      setBulkStep('select');
    } catch (err) {
      if (err && err.status === 401) { logout(); navigate('/auth', { replace: true }); return; }
      if (err && err.status === 409) {
        setBulkError(errorMessage(err, 'Massenimport wurde bereits durchgeführt.'));
        setBulkImportDone(true);
        return;
      }
      setBulkError(errorMessage(err, 'Fehler beim Laden der Archidekt-Decks.'));
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkStartImport = async () => {
    const deckIds = [...bulkSelectedIds];
    if (deckIds.length === 0) {
      setBulkError('Bitte mindestens ein Deck auswählen.');
      return;
    }
    setBulkLoading(true);
    setBulkError(null);
    try {
      await json('/api/decks/import/archidekt/bulk-start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deckIds }),
      });
      setBulkStep('importing');
      startBulkPolling();
    } catch (err) {
      if (err && err.status === 401) { logout(); navigate('/auth', { replace: true }); return; }
      setBulkError(errorMessage(err, 'Fehler beim Starten des Imports.'));
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkToggleDeck = (deckId) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(deckId)) next.delete(deckId); else next.add(deckId);
      return next;
    });
  };

  const handleBulkSelectAll = () => {
    setBulkSelectedIds(new Set(bulkDecks.map((d) => d.id)));
  };

  const handleBulkDeselectAll = () => {
    setBulkSelectedIds(new Set());
  };

  const handleCloseBulkModal = () => {
    if (bulkStep === 'importing') return; // Nicht schließbar während Import
    setShowNewDeckModal(false);
    setBulkStep('input');
    setBulkUsername('');
    setBulkDecks([]);
    setBulkSelectedIds(new Set());
    setBulkError(null);
    setBulkStatus(null);
    if (bulkPollRef.current) { clearInterval(bulkPollRef.current); bulkPollRef.current = null; }
  };

  const handleBulkBack = () => {
    setBulkStep('input');
    setBulkError(null);
  };

  const handleDeleteDeck = async (deckId) => {
    setLoading(true);
    setError(null);
    try {
      await json(`/api/decks/${deckId}`, { method: 'DELETE' });
      setShowDeleteConfirmModal(false);
      setDeckToDelete(null);
      fetchDecks();
    } catch (err) {
      if (err && err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('Fehler beim Löschen des Decks:', err);
      setError(errorMessage(err, 'Deck konnte nicht gelöscht werden.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyDeckToVirtual = async (deckId) => {
    setLoading(true);
    setError(null);
    try {
      const url = viewedUserId 
        ? `/api/decks/${deckId}/copy-to-virtual?userId=${viewedUserId}`
        : `/api/decks/${deckId}/copy-to-virtual`;
      
      await json(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      fetchDecks();
    } catch (err) {
      if (err && err.status === 401) {
        logout();
        navigate('/auth', { replace: true });
        return;
      }
      console.error('Fehler beim Kopieren des Decks:', err);
      setError(errorMessage(err, 'Deck konnte nicht kopiert werden.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ProfileBanner />
      <DeckListPage
        decks={decks}
        showNewDeckModal={showNewDeckModal}
        setShowNewDeckModal={setShowNewDeckModal}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        loading={loading}
        error={error}
        newDeckName={newDeckName}
        setNewDeckName={setNewDeckName}
        newDeckScryfallIds={newDeckScryfallIds}
        setNewDeckScryfallIds={setNewDeckScryfallIds}
        handleCreateDeck={handleCreateDeck}
        confirmDeleteDeck={confirmDeleteDeck}
        showDeleteConfirmModal={showDeleteConfirmModal}
        deckToDelete={deckToDelete}
        handleDeleteDeck={handleDeleteDeck}
        handleCancelDeleteDeck={handleCancelDeleteDeck}
        handleCopyDeckToVirtual={handleCopyDeckToVirtual}
        isOwnProfile={isOwnProfile}
        onRefresh={fetchDecks}
        archidektInput={archidektInput}
        setArchidektInput={setArchidektInput}
        importLoading={importLoading}
        importError={importError}
        setImportError={setImportError}
        handleArchidektImport={handleArchidektImport}
        bulkUsername={bulkUsername}
        setBulkUsername={setBulkUsername}
        bulkStep={bulkStep}
        bulkDecks={bulkDecks}
        bulkSelectedIds={bulkSelectedIds}
        bulkLoading={bulkLoading}
        bulkError={bulkError}
        bulkStatus={bulkStatus}
        bulkImportDone={bulkImportDone}
        handleBulkFetchDecks={handleBulkFetchDecks}
        handleBulkStartImport={handleBulkStartImport}
        handleBulkToggleDeck={handleBulkToggleDeck}
        handleBulkSelectAll={handleBulkSelectAll}
        handleBulkDeselectAll={handleBulkDeselectAll}
        handleCloseBulkModal={handleCloseBulkModal}
        handleBulkBack={handleBulkBack}
      />
    </>
  );
}
